import { Component, Inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  CountryISO,
  PhoneNumberFormat,
  SearchCountryField,
} from "ngx-intl-tel-input";
import { NgxIntlTelInputModule } from "ngx-intl-tel-input";
import Swal from "sweetalert2";

import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";
import { GloPhonenumberInputComponent } from "../../../shared/components/glo-phonenumber-input/glo-phonenumber-input.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { Customer } from "../../../shared/models/customer.model";
import { Salon } from "../../../shared/models/salon.model";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { ApiService } from "../../../shared/services/api/api.service";
import { environment } from "../../../../environments/environment";

type CustomerAddTab = "overview" | "crm" | "notes" | "profile";

@Component({
  selector: "business-customer-add",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GloControlComponent,
    GloPhonenumberInputComponent,
    NgxIntlTelInputModule,
    LoaderComponent,
  ],
  templateUrl: "./customer-add.component.html",
  styleUrl: "./customer-add.component.scss",
})
export class BusinessCustomerAddComponent {
  assetUrl = environment.assetBaseUrl;

  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;

  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  customCssClass = "form-control rounded-[8px] w-full block";

  customer_id: string | null = null;
  isEditMode = false;
  isLoading = false;
  salons: Salon[] = [];
  passwordFieldType: "password" | "text" = "password";

  activeTab: CustomerAddTab = "overview";

  tabs: { key: CustomerAddTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "crm", label: "CRM Details" },
    { key: "notes", label: "Notes" },
    { key: "profile", label: "Profile" },
  ];

  customerForm: FormGroup;
  previewUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private ApiService: ApiService,
    private dialogRef: MatDialogRef<BusinessCustomerAddComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.customerForm = this.fb.group({
      customer_id: [""],
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      password: [""],
      phone_number: ["", Validators.required],
      salon_id: ["", Validators.required],
      birthday: [""],
      member_level: [""],
      preferred_staff: [""],
      preferred_services: [""],
      notes: [""],
      image: [[]],
    });

    this.customer_id = data?.customer_id || null;
    this.isEditMode = !!this.customer_id;

    if (this.isEditMode) {
      this.updateCustomerForm(data);
      this.customerForm.get("email")?.disable();
    } else {
      this.customerForm.get("email")?.enable();
      this.applyPasswordValidators();
    }
  }

  ngOnInit(): void {
    this.loadSalons();
  }

  applyPasswordValidators(): void {
    const passwordControl = this.customerForm.get("password");

    passwordControl?.setValidators([
      Validators.required,
      Validators.minLength(6),
    ]);

    passwordControl?.updateValueAndValidity();
  }

  togglePasswordVisibility(): void {
    this.passwordFieldType =
      this.passwordFieldType === "password" ? "text" : "password";
  }

  loadSalons(): void {
    this.isLoading = true;

    this.ApiService.request(
      API_ENDPOINTS.salons.list,
      "GET",
      null,
      //   "Salons Loaded Successfully",
    ).subscribe({
      next: (response: any) => {
        this.salons = response.results || [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error("Error loading salons:", error);
        this.isLoading = false;
      },
    });
  }

  updateCustomerForm(data: any): void {
    this.customerForm.patchValue({
      customer_id: data.customer_id || "",
      firstName: data.firstName || data.first_name || "",
      lastName: data.lastName || data.last_name || "",
      email: data.email || "",
      password: data.password || "",
      phone_number: data.phone_number || "",
      image: data.image || [],
      salon_id: data.salon_id || "",
      birthday: data.birthday || "",
      member_level: data.member_level || data.member_Level || "",
      preferred_staff: data.preferred_staff || "",
      preferred_services: Array.isArray(data.preferred_services)
        ? data.preferred_services.join(", ")
        : data.preferred_services || "",
      notes: data.notes || data.internal_notes || "",
    });

    this.previewUrl = data.avatar || data.image || null;
  }

  handleFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    this.customerForm.get("image")?.setValue(file);
    this.previewImage(file);
  }

  handleDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();

    if (!event.dataTransfer || !event.dataTransfer.files.length) return;

    const file = event.dataTransfer.files[0];

    this.customerForm.get("image")?.setValue(file);
    this.previewImage(file);
  }

  previewImage(file: File): void {
    const reader = new FileReader();

    reader.onload = () => {
      this.previewUrl = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  checkValidField(fieldName: string): boolean {
    const formField = this.customerForm.get(fieldName);

    if (!formField) return true;

    return !(formField.invalid && formField.touched);
  }

  handleSubmit(): void {
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      this.goToFirstInvalidTab();
      return;
    }

    const rawData = this.customerForm.getRawValue();

    const customerData: any = {
      ...rawData,
      phone_number:
        rawData.phone_number?.e164Number || rawData.phone_number || "",
      preferred_services:
        typeof rawData.preferred_services === "string"
          ? rawData.preferred_services
              .split(",")
              .map((item: string) => item.trim())
              .filter(Boolean)
          : rawData.preferred_services,
      internal_notes: rawData.notes,
    };

    this.isLoading = true;

    if (this.isEditMode) {
      this.updateCustomer(customerData);
    } else {
      this.createCustomer(customerData);
    }
  }

  private goToFirstInvalidTab(): void {
    const overviewFields = [
      "firstName",
      "lastName",
      "email",
      "phone_number",
      "password",
    ];

    const crmFields = ["salon_id"];

    if (
      overviewFields.some(
        (fieldName) => this.customerForm.get(fieldName)?.invalid,
      )
    ) {
      this.activeTab = "overview";
      return;
    }

    if (
      crmFields.some((fieldName) => this.customerForm.get(fieldName)?.invalid)
    ) {
      this.activeTab = "crm";
    }
  }

  private createCustomer(data: Customer): void {
    this.ApiService.request(
      API_ENDPOINTS.owner.customers.create,
      "POST",
      data,
      "Customer Created Successfuly!",
    ).subscribe({
      next: () => {
        this.isLoading = false;
        this.onClose();

        Swal.fire({
          title: "Customer Created Successfully!",
          icon: "success",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      },
      error: () => {
        this.isLoading = false;

        Swal.fire({
          title: "Failed to Create Customer",
          text: "Error while creating customer. Please try again.",
          icon: "error",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      },
    });
  }

  updateCustomer(data: Customer): void {
    this.ApiService.request(
      API_ENDPOINTS.owner.customers.update(String(this.customer_id)),
      "PATCH",
      data,
      "Customer Updated Successfuly!",
    ).subscribe({
      next: () => {
        this.isLoading = false;
        this.onClose();

        Swal.fire({
          title: "Customer Updated Successfully!",
          icon: "success",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      },
      error: () => {
        this.isLoading = false;

        Swal.fire({
          title: "Failed to Update Customer",
          text: "Error while updating customer. Please try again.",
          icon: "error",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      },
    });
  }

  get modalTitle(): string {
    return this.isEditMode ? "Edit Customer" : "Add Customer";
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
